const STORAGE_KEY = 'dynamicEventsFirstTouch';
const SEARCH_ENGINE_HOSTNAME_FRAGMENTS = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'baidu.'];

// Records the referrer and landing URL for this browser session the first time it's called,
// then no-ops on every subsequent call (incl. Gatsby client-side route changes) so later
// internal navigation never overwrites the visitor's true first touch.
export const captureFirstTouch = () => {
  if (typeof window === 'undefined') return;
  if (window.sessionStorage.getItem(STORAGE_KEY) !== null) return;

  const firstTouch = {
    referrer: document.referrer || '',
    landingPageUrl: window.location.href
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(firstTouch));
  } catch (error) {
    console.warn('Unable to persist first-touch attribution', error);
  }
};

const readStoredFirstTouch = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

const landingPageParams = (landingPageUrl) => {
  try {
    return new URL(landingPageUrl).searchParams;
  } catch (error) {
    return new URLSearchParams('');
  }
};

const referrerIsSearchEngine = (referrer) => {
  try {
    const hostname = new URL(referrer).hostname;
    return SEARCH_ENGINE_HOSTNAME_FRAGMENTS.some((fragment) => hostname.includes(fragment));
  } catch (error) {
    return false;
  }
};

// The single value to show in Pardot's existing "Website Referral URL" field. Ad/campaign
// clicks often carry the same generic referrer as an organic click (e.g. both show
// "google.com"), so a tagged landing URL (utm_*/gclid/msclkid) is the more specific signal and
// wins when present. Otherwise fall back to the plain referrer, then blank for genuine direct
// visits.
export const getFirstTouchReferralUrl = () => {
  const firstTouch = readStoredFirstTouch();
  if (!firstTouch) return '';

  const params = landingPageParams(firstTouch.landingPageUrl);
  const hasTrackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'msclkid'].some(
    (key) => !!params.get(key)
  );
  if (hasTrackingParams) return firstTouch.landingPageUrl;

  return firstTouch.referrer || '';
};

// A human-readable classification of the same first-touch signals, so the team doesn't have to
// eyeball a raw URL to tell paid and organic apart (they often share the same referrer domain).
export const getTrafficSourceType = () => {
  const firstTouch = readStoredFirstTouch();
  if (!firstTouch) return '';

  const params = landingPageParams(firstTouch.landingPageUrl);

  if (params.get('gclid') || params.get('msclkid')) return 'Paid Search';

  const utmMedium = params.get('utm_medium') || '';
  const utmSource = params.get('utm_source') || '';
  if (/email/i.test(utmMedium) || /email/i.test(utmSource)) return 'Email';

  if (utmSource || utmMedium || params.get('utm_campaign')) return 'Campaign';

  if (firstTouch.referrer) {
    return referrerIsSearchEngine(firstTouch.referrer) ? 'Organic Search' : 'Referral';
  }

  return 'Direct';
};
